const D = console.log
const E = console.error

export type APIVersion = 'V1' | 'V2'

////////////////////////////////////////////////////////////////////////////////
// NETWORK-RELATED utilities
////////////////////////////////////////////////////////////////////////////////

/**
 * Detect if IP address is valid IPv4 or IPv6 address
 */
export function isIP (address : string) : number {
    if (isIPv4(address)) {
        return 4
    } else if (isIPv6(address)) {
        return 6
    }
    return 0
}

function isIPv4(address:string) : boolean {
    // Special cases for shorthand IPv4 notations
    if (address === '0' || address === '127') {
        return true
    }

    const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/
    return ipv4Regex.test(address)
}

function isIPv6(address:string) : boolean {
    const ipv6Regex = /^(?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}$|^(?:[a-fA-F0-9]{1,4}:){1,7}:$|^(?:[a-fA-F0-9]{1,4}:){1,6}:[a-fA-F0-9]{1,4}$|^(?:[a-fA-F0-9]{1,4}:){1,5}(?::[a-fA-F0-9]{1,4}){1,2}$|^(?:[a-fA-F0-9]{1,4}:){1,4}(?::[a-fA-F0-9]{1,4}){1,3}$|^(?:[a-fA-F0-9]{1,4}:){1,3}(?::[a-fA-F0-9]{1,4}){1,4}$|^(?:[a-fA-F0-9]{1,4}:){1,2}(?::[a-fA-F0-9]{1,4}){1,5}$|^(?:[a-fA-F0-9]{1,4}:){1,1}(?::[a-fA-F0-9]{1,4}){1,6}$|^:(?::[a-fA-F0-9]{1,4}){1,7}$|^(?:(?:[a-fA-F0-9]{1,4}:){1,7}|:):(?::(?:[a-fA-F0-9]{1,4}:){0,5}:|(?:[a-fA-F0-9]{1,4}:){0,6}[a-fA-F0-9]{1,4})$/
    return ipv6Regex.test(address)
}

function iptest () {
  function testIP(address:string, expected:number) {
      const result = isIP(address)
      const passed = result === expected
      console.log(`Testing: ${address}\nExpected: IPv${expected}\nActual: IPv${result}\n${passed ? '✅ Test Passed' : '❌ Test Failed'}\n`)
  }

  // Test cases
  testIP("192.168.0.1", 4)
  testIP("2001:0db8:85a3:0000:0000:8a2e:0370:7334", 6)
  testIP("::1", 6)
  testIP("2001:db8::8a2e:370:7334", 6)
  testIP("invalid-ip", 0)
  testIP("0", 4)
  testIP("127", 4)
}

/**
 *  URL-oriented utilities
 */
export class UOurL {
    /**
     * MODIFY pathname of URL to remove trailing and duplicate path separators and all leading/trailing whitespace
     * (source URL is untouched)
     */
    static normalize (url: URL) : URL {
        url = new URL(url)
        const path = url.pathname
        url.pathname =
            path
                .trim()
                .replace(/^\/+/,'')
                .replace(/\/+$/,'')
                .replace(/\/+/g,'/')
                .trim()
        return url
    }

    /**
     * PARSE operational HOST/URL
     * Supports simplitied host:port format
     */
    static parseURL (s:string|undefined) : URL | undefined {
        let url: URL | undefined
        if (s) {
            // LEGAL URL
            if (s.match(/^http(s)?:/)) {
                try {
                    url = new URL(s)
                }
                catch (e) {
                    E(`Illegal URL form: ${url}`,e)
                }
            }
            // HOST:PORT
            else {
                const m = s.match(/^[^:]+:(\d+)$/)
                if (m) {
                    // APPEND scheme
                    try {
                        url = new URL(`http://${s}`)
                    }
                    catch (e) {
                        E(`Illegal URL form: ${url}`,e)
                    }
                }
                else {
                    E('Illegal HOST/URL form',s)
                }
            }
        }
        return url
    }

    /**
     * SERIALIZE to string, supporting simplified host:port format when possible
     * @param url 
     */
    static toString (url: URL) : string {
        return url.pathname.length < 2 && url.protocol === 'http:'
            // 1) host+port
            ? `${url.hostname}${url.port ? ':' + url.port : ''}`
            // 2) URL
            : String(url)
    }

    /**
     * FETCH URL with timeout
     * @param url
     * @param options fetch options (as would be supported by native fetch)
     * @param timeout 
     * @throws AbortError upon timeout (error.name == `AbortError`)
     */
    static async fetch (
        {url, options = {}, timeout,verbose} : {url:string|URL,options?:any,timeout:number,verbose?:true})
            : Promise<Response> 
    {
        url = new URL(url)
        if (verbose) {
            D('FETCHING',options.method ?? 'GET',String(url))
        }
        // AbortController needed to cancel "timed out" fetch request
        const controller = new AbortController()
        const {signal} = controller

        // ABORT fetch after specified timeout
        const timer = setTimeout(() => controller.abort({error:'TIMEOUT',url}), timeout)
        // FETCH request (with signal from AbortController)
        try {
            const response = await fetch(url, { ...options, signal })
            // ABORT timeout timer
            clearTimeout(timer)
            return response
        }
        catch (e) {
            // ABORT timeout timer
            clearTimeout(timer)
            throw e
        }
    }
}

/**
 * Simple wrapper around ProPatch API
 */
export class PPApi {
    /**
     * PUSH route set to legacy API
     */
    static async pushRoutes ({host,proxy,routes,timeout,version}:{host:URL,proxy?:URL,routes:string,timeout:number,version:APIVersion}) {
        const path = version === 'V2' ? '/api/v2/route' : '/route'
        const url = PPApi.remoteURL({base:host,proxy,path,params:{paths: routes}})
        D(`ROUTE PUSH ${routes}`,url)
        return await UOurL.fetch({
            url,
            options: {
                method: 'PUT',
                headers: { 'Content-Length': '0' }
            },
            timeout,
            verbose: true,
        })
    }

    /**
     * NEW ping endpoint for health check (returns 204)
     */
    static async ping ({host,proxy,timeout}:{host:URL,proxy?:URL,timeout:number}) {
        const url = PPApi.remoteURL({base:host,proxy,path:'/ping'})
        return await UOurL.fetch({
            url,
            options: { method: 'GET' },
            timeout,
        })
    }

    /**
     * LEGACY ping using PUT /route (expects 400 from badly formed request)
     */
    static async pingLegacy ({host,proxy,timeout}:{host:URL,proxy?:URL,timeout:number}) {
        const url = PPApi.remoteURL({base:host,proxy,path:'/route'})
        return await UOurL.fetch({
            url,
            options: {
                method: 'PUT',
                headers: { 'Content-Length': '0' }
            },
            timeout,
        })
    }

    /** 
     * PRODUCE API operation URL from base URL, operation path and query parameter(s)
     */
    static remoteURL ({base,proxy,path,params:$params={}}:{base:URL,proxy?:URL,path:string,params?:Record<string,string>}) : string {
        const url = new URL(base)
        const params = Object.entries($params)
        url.pathname = url
            // BASE URL (sans trailing '/')
            .pathname.replace(/\/+$/,'')
            + '/'
            // OPERATION PATH (sans leading '/')
            + path.replace(/^\/+/,'')
        if (params.length) {
            params.forEach(([k,v]) => url.searchParams.set(k,v))
        }

        return (proxy ? (String(proxy).replace(/\/+$/,'') + '/') : '')
            // THIS IS SILLINESS - only here until certain PP API is properly URI decoding query params
            + String(url).replace(/%2C/g,',')
    }
}

/**
 * MONITOR health of HTTP API endpoint
 *
 * Uses /ping endpoint (expects 204) with fallback to legacy PUT /route (expects 400)
 * for backward compatibility with direct ProPatch connections.
 */
export class HttpApiMonitor {
    // sequential connection failures (since last success)
    fails: number = 0
    // report state changes (true => healthy connection; false => bad connection)
    onChange: (state:boolean) => void
    // MAX count of consecutive ping fails before considered "BAD"
    maxFails: number
    // termination state
    terminated: boolean = false
    // POLLING interval
    pollInt: number
    verbose?: true
    // connection parameters for ping operations
    private host: URL
    private proxy?: URL
    private timeout: number
    // use legacy PUT /route instead of GET /ping (for direct PP connections)
    private useLegacy: boolean = false

    constructor (
            {host,proxy,timeout,maxFails,pollInt,verbose,onChange} :
            {
                host: URL,
                proxy?: URL,
                timeout: number,
                maxFails: number,
                pollInt: number,
                verbose?: true,
                onChange: (state:boolean) => void
            }) {
        this.host = host
        this.proxy = proxy
        this.timeout = timeout
        this.maxFails = maxFails
        this.pollInt = pollInt
        this.onChange = onChange
        this.verbose = verbose
        // START in "bad" state (before polling begins)
        this.onChange(false)
        this.fails = this.maxFails + 1
        // BEGIN API polling
        this.poll()
    }

    /**
     * COUNT strike against "good" state
     * On strikeout (maxFails reached), reset to modern mode
     */
    private strike () {
        if (++this.fails >= this.maxFails) {
            // API officially unresponsive - reset state and try modern again
            this.onChange(false)
            this.useLegacy = false
        }
    }

    /**
     * MARK connection as good
     */
    private good () {
        this.fails = 0
        this.onChange(true)
    }

    /**
     * SCHEDULE next poll
     */
    private nextPoll (immediate?: boolean) {
        if (!this.terminated) {
            setTimeout(() => this.poll(), immediate ? 0 : this.pollInt)
        }
    }

    /**
     * POLL API health
     *
     * Algorithm:
     * - If in legacy mode: use PUT /route, expect 400
     *   - 400 → good, stay in legacy
     *   - non-400 → strike, switch to /ping
     *   - error/timeout → strike, stay in legacy
     *
     * - If in modern mode: use GET /ping, expect 204
     *   - 204 → good
     *   - timeout → strike, stay in /ping, immediate retry
     *   - non-204 → immediately try legacy:
     *     - 400 → good, switch to legacy mode
     *     - timeout → strike, stay in /ping, immediate retry
     *     - else → strike, stay in /ping
     *
     * - On strikeout (maxFails reached): reset to modern mode
     */
    poll () {
        if (this.terminated) return
        if (this.verbose) D('API MONITOR: POLL', this.useLegacy ? '(legacy)' : '(modern)')

        if (this.useLegacy) {
            this.pollLegacy()
        } else {
            this.pollModern()
        }
    }

    /**
     * POLL using modern GET /ping endpoint
     */
    private pollModern () {
        PPApi.ping({host: this.host, proxy: this.proxy, timeout: this.timeout})
            .then(response => {
                if (this.terminated) return
                if (response.status === 204) {
                    this.good()
                    this.nextPoll()
                } else {
                    // Non-204: immediately try legacy fallback
                    if (this.verbose) D('API MONITOR: /ping returned', response.status, '- trying legacy')
                    this.tryLegacyFallback()
                }
            })
            .catch((e) => {
                if (this.terminated) return
                if (this.verbose) D('API MONITOR: /ping error', e)
                // Timeout or network error: strike, stay in modern mode, retry
                this.strike()
                this.nextPoll(e?.name === 'AbortError')
            })
    }

    /**
     * TRY legacy fallback (called immediately when /ping returns non-204)
     */
    private tryLegacyFallback () {
        PPApi.pingLegacy({host: this.host, proxy: this.proxy, timeout: this.timeout})
            .then(response => {
                if (this.terminated) return
                if (response.status === 400) {
                    // Legacy works - switch to legacy mode permanently
                    if (this.verbose) D('API MONITOR: legacy works, switching to legacy mode')
                    this.useLegacy = true
                    this.good()
                    this.nextPoll()
                } else {
                    // Unexpected status from legacy too
                    this.strike()
                    this.nextPoll()
                }
            })
            .catch((e) => {
                if (this.terminated) return
                if (this.verbose) D('API MONITOR: legacy fallback error', e)
                // Timeout on legacy: strike, but DON'T switch to legacy, try /ping again
                this.strike()
                this.nextPoll(e?.name === 'AbortError')
            })
    }

    /**
     * POLL using legacy PUT /route endpoint
     */
    private pollLegacy () {
        PPApi.pingLegacy({host: this.host, proxy: this.proxy, timeout: this.timeout})
            .then(response => {
                if (this.terminated) return
                if (response.status === 400) {
                    this.good()
                    this.nextPoll()
                } else {
                    this.strike()
                    this.useLegacy = false  // try modern again on next poll
                    this.nextPoll()
                }
            })
            .catch((e) => {
                if (this.terminated) return
                if (this.verbose) D('API MONITOR: legacy error', e)
                // error/timeout: strike but stay in legacy (strikeout will reset to modern)
                this.strike()
                this.nextPoll(e?.name === 'AbortError')
            })
    }

    /**
     * TERMINATE polling
     */
    terminate () {
        D('API MONITOR: TERMINATED')
        this.onChange(false)
        this.terminated = true
    }
}
